export const SUBSCRIPTION_END_TIMEZONE = "America/Bogota";

export interface SubscriptionEndInput {
  grace_period_ends_at?: string | null;
  cancel_scheduled_for?: string | null;
  last_charge_error?: string | null;
  retry_count?: number | null;
}

export interface SubscriptionEndAuthorization {
  status?: string | null;
  mode?: string | null;
}

export type SubscriptionEnd =
  | { kind: "none" }
  | { kind: "cancelled"; activeUntil: string }
  | { kind: "paused"; activeUntil: string }
  | { kind: "payment_failed"; suspendOn: string }
  | { kind: "scheduled"; cancelOn: string };

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const longDate = new Intl.DateTimeFormat("es-CO", {
  timeZone: SUBSCRIPTION_END_TIMEZONE,
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function authorizationPaused(authorization: SubscriptionEndAuthorization | null | undefined): boolean {
  return authorization?.status === "paused" && authorization.mode !== "per_use_card_capture";
}

export function subscriptionEnd(
  subscription: SubscriptionEndInput | null | undefined,
  authorization?: SubscriptionEndAuthorization | null,
): SubscriptionEnd {
  if (!subscription) return { kind: "none" };
  if (subscription.grace_period_ends_at) {
    const chargeFailed = Boolean(subscription.last_charge_error) || (subscription.retry_count ?? 0) > 0;
    if (chargeFailed) return { kind: "payment_failed", suspendOn: subscription.grace_period_ends_at };
    return authorizationPaused(authorization)
      ? { kind: "paused", activeUntil: subscription.grace_period_ends_at }
      : { kind: "cancelled", activeUntil: subscription.grace_period_ends_at };
  }
  if (subscription.cancel_scheduled_for) return { kind: "scheduled", cancelOn: subscription.cancel_scheduled_for };
  return { kind: "none" };
}

export function formatBogotaLongDate(value: string): string {
  const date = new Date(DATE_ONLY.test(value) ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return value;
  const parts = Object.fromEntries(longDate.formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.day} de ${parts.month} de ${parts.year}`;
}

export function cancelledHeadline(activeUntil: string): string {
  return `Suscripción cancelada — activa hasta el ${formatBogotaLongDate(activeUntil)}`;
}

export const CANCELLED_DETAIL = "No se harán más cobros. Tu plan sigue funcionando hasta esa fecha.";

export function pausedHeadline(activeUntil: string): string {
  return `Suscripción pausada en Mercado Pago — activa hasta ${formatBogotaLongDate(activeUntil)}`;
}

export const PAUSED_DETAIL = "Reactívala en Mercado Pago o cambia tu tarjeta para seguir usando Flow después de esa fecha.";

export function endHeadline(end: { kind: "cancelled" | "paused"; activeUntil: string }): string {
  return end.kind === "paused" ? pausedHeadline(end.activeUntil) : cancelledHeadline(end.activeUntil);
}

export function endDetail(end: { kind: "cancelled" | "paused" }): string {
  return end.kind === "paused" ? PAUSED_DETAIL : CANCELLED_DETAIL;
}

export function daysUntil(value: string, now: Date = new Date()): number {
  const end = new Date(DATE_ONLY.test(value) ? `${value}T12:00:00Z` : value);
  return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}
